import { ErrorOutline } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FilterConfig, Node as K8sNode } from '../k8s-analyzer-types';
import { K8sAnalyzerNodesListFilterPanel } from './k8s-analyzer-nodes-list-filter-panel';
import { K8sAnalyzerNodesListRefreshButton, useNodesFetch } from './k8s-analyzer-nodes-list-refresh-button';
import { K8sAnalyzerNodesListTableNodeRow } from './k8s-analyzer-nodes-list-table-node-row';
import { K8sAnalyzerNodesListTablePodRow } from './k8s-analyzer-nodes-list-table-pod-row';
import { calculatePercentage } from './k8s-analyzer-nodes-list.utils';
import { K8sAnalyzerHighMemoryAlert } from './k8s-analyzer-nodes-list-pod-dedecated-for-sigterm';
import { K8sAnalyzerNodesListStatistics } from './k8s-analyzer-nodes-list-statistics';

interface SortConfig {
    key: string;
    direction: 'asc' | 'desc';
}

export interface UseNodesFetchResult {
    nodes: K8sNode[];
    isLoading: boolean;
    isRefreshing: boolean;
    connectionError: string | null;
    retryCount: number;
    retry: () => Promise<void>;
}

export function K8sAnalyzerNodesList() {
    const {
        nodes,
        isLoading,
        isRefreshing,
        connectionError,
        retryCount,
        retry
    } = useNodesFetch();

    const [nodeFilter, setNodeFilter] = useState<FilterConfig>({
        enabled: true,
        cpuThreshold: '0',
        memoryThreshold: '0',
        memorySize: '',
        memoryUnit: 'Gi',
        memoryCondition: 'greaterThan',
        nameFilter: ''
    });

    const [podFilter, setPodFilter] = useState<FilterConfig>({
        enabled: true,
        cpuThreshold: '0',
        memoryThreshold: '0',
        memorySize: '',
        memoryUnit: 'Gi',
        memoryCondition: 'greaterThan',
        nameFilter: 'tudo-helm-694654fc5d-zd4wm'
    });

    const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'memPercent', direction: 'desc' });

    // Initialize expanded nodes when the nodes list changes
    // useEffect(() => {
    //     const newExpandedNodes: Record<string, boolean> = {};
    //     nodes.forEach(node => {
    //         newExpandedNodes[node.name] = true;
    //     });
    //     setExpandedNodes(newExpandedNodes);
    // }, [nodes]);

    const toggleNodeExpansion = (nodeName: string) => {
        setExpandedNodes(prev => ({
            ...prev,
            [nodeName]: !prev[nodeName]
        }));
    };

    const handleSort = (key: string) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const getSortedNodes = (nodes: K8sNode[]) => {
        return [...nodes].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortConfig.key) {
                case 'name':
                    aValue = a.name;
                    bValue = b.name;
                    break;
                case 'status':
                    aValue = a.status;
                    bValue = b.status;
                    break;
                case 'role':
                    aValue = a.role || '';
                    bValue = b.role || '';
                    break;
                case 'taints':
                    aValue = a.taints || 0;
                    bValue = b.taints || 0;
                    break;
                case 'version':
                    aValue = a.version;
                    bValue = b.version;
                    break;
                case 'pods':
                    aValue = a.pods || 0;
                    bValue = b.pods || 0;
                    break;
                case 'cpu':
                    aValue = parseFloat(a.metrics.cpu.usage);
                    bValue = parseFloat(b.metrics.cpu.usage);
                    break;
                case 'mem':
                    aValue = parseFloat(a.metrics.memory.usage);
                    bValue = parseFloat(b.metrics.memory.usage);
                    break;
                case 'cpuPercent':
                    aValue = calculatePercentage(a.metrics.cpu.usage, a.metrics.cpu.capacity, 'cpu');
                    bValue = calculatePercentage(b.metrics.cpu.usage, b.metrics.cpu.capacity, 'cpu');
                    break;
                case 'memPercent':
                    aValue = calculatePercentage(a.metrics.memory.usage, a.metrics.memory.capacity, 'memory');
                    bValue = calculatePercentage(b.metrics.memory.usage, b.metrics.memory.capacity, 'memory');
                    break;
                case 'cpuAlloc':
                    aValue = parseFloat(a.metrics.cpu.capacity);
                    bValue = parseFloat(b.metrics.cpu.capacity);
                    break;
                case 'memAlloc':
                    aValue = parseFloat(a.metrics.memory.capacity);
                    bValue = parseFloat(b.metrics.memory.capacity);
                    break;
                case 'age':
                    aValue = a.age || '';
                    bValue = b.age || '';
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const filteredNodes = getSortedNodes(nodes.filter(node => {
        // Node filtering
        if (nodeFilter.enabled) {
            const nodeCpuThreshold = parseFloat(nodeFilter.cpuThreshold);
            const nodeMemoryThreshold = parseFloat(nodeFilter.memoryThreshold);
            const hasNodeCpuFilter = !isNaN(nodeCpuThreshold) && nodeCpuThreshold > 0;
            const hasNodeMemoryFilter = !isNaN(nodeMemoryThreshold) && nodeMemoryThreshold > 0;
            const hasNodeNameFilter = nodeFilter.nameFilter.trim() !== '';

            if (hasNodeNameFilter && !node.name.toLowerCase().includes(nodeFilter.nameFilter.toLowerCase().trim())) {
                return false;
            }

            if (hasNodeCpuFilter || hasNodeMemoryFilter) {
                const cpuPercentage = calculatePercentage(node.metrics.cpu.usage, node.metrics.cpu.capacity, 'cpu');
                const memPercentage = calculatePercentage(node.metrics.memory.usage, node.metrics.memory.capacity, 'memory');

                if (hasNodeCpuFilter && cpuPercentage < nodeCpuThreshold) {
                    return false;
                }

                if (hasNodeMemoryFilter && memPercentage < nodeMemoryThreshold) {
                    return false;
                }
            }
        }

        // Pod filtering
        if (podFilter.enabled && node.pods_list && node.pods_list.length > 0) {
            // Filter the pods list first
            const filteredPods = node.pods_list.filter(pod => {
                const podCpuThreshold = parseFloat(podFilter.cpuThreshold);
                const podMemoryThreshold = parseFloat(podFilter.memoryThreshold);
                const hasPodCpuFilter = !isNaN(podCpuThreshold) && podCpuThreshold > 0;
                const hasPodMemoryFilter = !isNaN(podMemoryThreshold) && podMemoryThreshold > 0;
                const hasPodNameFilter = podFilter.nameFilter.trim() !== '';

                if (hasPodNameFilter && !pod.name.toLowerCase().includes(podFilter.nameFilter.toLowerCase().trim())) {
                    return false;
                }

                if (hasPodCpuFilter || hasPodMemoryFilter) {
                    const podCpuPercentage = calculatePercentage(pod.metrics.cpu.usage, pod.metrics.cpu.capacity, 'cpu');
                    const podMemPercentage = calculatePercentage(pod.metrics.memory.usage, pod.metrics.memory.capacity, 'memory');

                    if (hasPodCpuFilter && podCpuPercentage < podCpuThreshold) {
                        return false;
                    }

                    if (hasPodMemoryFilter && podMemPercentage < podMemoryThreshold) {
                        return false;
                    }
                }

                return true;
            });

            // Update the node's pods_list with the filtered pods
            node.pods_list = filteredPods;

            // If pod filtering is enabled and no pods match the criteria, exclude this node
            if (filteredPods.length === 0) {
                return false;
            }
        }

        return true;
    }));

    if (isLoading && !connectionError) {
        return (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Kubernetes Nodes</Typography>
                <K8sAnalyzerNodesListRefreshButton />
            </Box>

            {!isLoading && !connectionError && <K8sAnalyzerNodesListStatistics nodes={nodes} />}

            {connectionError && (
                <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={retry}
                            disabled={isRefreshing}
                        >
                            Retry
                        </Button>
                    }
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ErrorOutline />
                        <Typography variant="body2">
                            {connectionError}
                            {retryCount > 0 && ` (Retry attempt ${retryCount}/3)`}
                        </Typography>
                    </Box>
                </Alert>
            )}

            <K8sAnalyzerHighMemoryAlert nodes={nodes} />

            <K8sAnalyzerNodesListFilterPanel
                nodeFilter={nodeFilter}
                podFilter={podFilter}
                onNodeFilterChange={setNodeFilter}
                onPodFilterChange={setPodFilter}
                totalNodes={nodes.length}
                filteredNodes={filteredNodes.length}
            />

            {nodes.length > 0 && filteredNodes.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, backgroundColor: '#fff3e0', borderRadius: 1, mb: 3 }}>
                    <Typography color="warning.main" sx={{ fontWeight: 'bold' }}>
                        No nodes match the current filter criteria
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Try adjusting the filter thresholds or disabling some filters
                    </Typography>
                </Box>
            ) : connectionError && nodes.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography color="text.secondary">
                        Unable to load node data. Please check your connection and try again.
                    </Typography>
                </Box>
            ) : filteredNodes.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography color="text.secondary">
                        No nodes found to display
                    </Typography>
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{
                    maxHeight: '70vh',
                    overflow: 'auto',
                    '& .MuiTableCell-root': {
                        padding: '6px 16px',
                        fontSize: '0.85rem',
                        borderBottom: '1px solid rgba(224, 224, 224, 0.4)'
                    },
                    '& .MuiTableHead-root .MuiTableCell-root': {
                        fontWeight: 'bold',
                        backgroundColor: 'rgba(0, 0, 0, 0.05)'
                    }
                }}>
                    <Box sx={{ p: 1, backgroundColor: '#e3f2fd', textAlign: 'center' }}>
                        <Typography variant="caption">
                            Showing {filteredNodes.length} of {nodes.length} nodes with current filters
                        </Typography>
                    </Box>

                    <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
                        <colgroup>
                            <col style={{ width: '40px' }} />
                            <col style={{ width: '30%' }} />
                            <col style={{ width: '80px' }} />
                            <col style={{ width: '80px' }} />
                            <col style={{ width: '80px' }} />
                            <col style={{ width: '120px' }} />
                            <col style={{ width: '60px' }} />
                            <col style={{ width: '60px' }} />
                            <col style={{ width: '80px' }} />
                            <col style={{ width: '60px' }} />
                            <col style={{ width: '60px' }} />
                            <col style={{ width: '60px' }} />
                            <col style={{ width: '60px' }} />
                            <col style={{ width: '60px' }} />
                        </colgroup>

                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell
                                    onClick={() => handleSort('name')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    NAME {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell
                                    onClick={() => handleSort('status')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    STATUS {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell
                                    onClick={() => handleSort('role')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    ROLE {sortConfig.key === 'role' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell
                                    onClick={() => handleSort('taints')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    TAINTS {sortConfig.key === 'taints' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell
                                    onClick={() => handleSort('version')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    VERSION {sortConfig.key === 'version' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    onClick={() => handleSort('pods')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    PODS {sortConfig.key === 'pods' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    onClick={() => handleSort('cpu')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    CPU {sortConfig.key === 'cpu' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    onClick={() => handleSort('mem')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    MEM {sortConfig.key === 'mem' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    onClick={() => handleSort('cpuPercent')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    %CPU {sortConfig.key === 'cpuPercent' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    onClick={() => handleSort('memPercent')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    %MEM {sortConfig.key === 'memPercent' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    onClick={() => handleSort('cpuAlloc')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    CPU/A {sortConfig.key === 'cpuAlloc' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    onClick={() => handleSort('memAlloc')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    MEM/A {sortConfig.key === 'memAlloc' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    onClick={() => handleSort('age')}
                                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                                >
                                    AGE {sortConfig.key === 'age' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredNodes.map((node) => (
                                <React.Fragment key={node.name}>
                                    <K8sAnalyzerNodesListTableNodeRow
                                        node={node}
                                        isExpanded={!!expandedNodes[node.name]}
                                        onToggleExpand={() => toggleNodeExpansion(node.name)}
                                    />
                                    {!!expandedNodes[node.name] && node.pods_list?.map((pod) => (
                                        <K8sAnalyzerNodesListTablePodRow
                                            key={pod.name}
                                            pod={pod}
                                        />
                                    ))}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
